import React from 'react'
import PropTypes from 'prop-types'
import { Redirect, Link } from 'react-router-dom'
import { withStyles } from '@material-ui/core/styles'
import { Page, Container, AppBar, IconButton, Button } from '@michelin/acid-components'
import { Grid, CircularProgress, Menu, MenuItem, Typography, Avatar } from '@material-ui/core'
import { Menu as MenuIcon, Clear as ClearIcon, Search as SearchIcon, SettingsPhone as SettingsPhoneIcon, ExitToApp as ExitToAppIcon } from '@material-ui/icons'
import MaterialInputable from './MaterialInputable'
import MaterialSelectable from './MaterialSelectable'
import AccountSelectionDialog from './AccountSelectionDialog'
import NewAccountDialog from './NewAccountDialog'
import Address from './Address'
import FormValidator from './FormValidator'
import api from '../api'
import { ToastContainer, toast } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.min.css'
import clsx from 'clsx'

const styles = theme => ({
	saving: {
		height: '100vh'
	},
	container: {
		paddingLeft: '12px',
		paddingRight: '24px',
		paddingTop: '60px',
		fontSize: '11px',
	},
	root: {
		flexGrow: 1
	},
	flex: {
		flexGrow: 1,
	},
	userInfo: {
		fontSize: '14px',
	},
	button: {
		margin: theme.spacing.unit,
	},
	extendedIcon: {
		marginRight: theme.spacing.unit,
	},
	leftIcon: {
		marginRight: theme.spacing.unit,
	},
	rightIcon: {
		marginLeft: theme.spacing.unit,
	},
	iconSmall: {
		fontSize: 20,
	},
	logo: {
		width: '4rem', 
		height: '4rem', 
		marginRight: '1rem',
	},
	menuButton: {
		marginRight: '1rem',
	},
})

const address = {
	addressLine1: 'addressLine1',
	addressLine2: 'addressLine2',
	city: 'city',
	state: 'state',
	zip: 'zip',
	country: 'country'
}

const dealerAddress = {
	addressLine1: 'dealerAddressLine1',
	addressLine2: 'dealerAddressLine2',
	city: 'dealerCity',
	state: 'dealerState',
	zip: 'dealerZip',
	country: 'country'
}

class FormDialog extends React.Component {
	constructor(props) {
		super(props)

		this.newAccountDialog = React.createRef()

		this.validator = new FormValidator([
			{
				prop: {
					object: 'searchObject',
					name: 'accountNumber'
				},
				field: 'accountNumber',
				method: this.hasOneOf,
				args: ['props.searchObject.accountNumber', 'props.searchObject.customerName', 'props.searchObject.dealerName'],
				validWhen: true,
				message: 'Account Number or a Name is required'
			},
			{
				prop: {
					object: 'searchObject',
					name: 'customerName'
				},
				field: 'customerName',
				method: this.hasOneOf,
				args: ['props.searchObject.accountNumber', 'props.searchObject.customerName', 'props.searchObject.dealerName'],
				validWhen: true,
				message: 'Account Number or a Name is required'
			},
			{
				prop: {
					object: 'searchObject',
					name: 'dealerName'
				},
				field: 'dealerName',
				method: this.hasOneOf,
				args: ['props.searchObject.accountNumber', 'props.searchObject.customerName', 'props.searchObject.dealerName'],
				validWhen: true,
				message: 'Account Number or a Name is required'
			},
		])

		this.state = {
			isClearing: false,
			openNewAccountDialog: false,
			anchorEl: null,
			validation: this.validator.valid()
		}

		this.handleKeyPress = this.handleKeyPress.bind(this)
		this.onClear = this.onClear.bind(this)
		this.onSearchClick = this.onSearchClick.bind(this)
		this.onNewAccount = this.onNewAccount.bind(this)
		this.closeNewAccountDialog = this.closeNewAccountDialog.bind(this)
		this.createNewAccount = this.createNewAccount.bind(this)
		this.handleMenuOpen = this.handleMenuOpen.bind(this)
		this.handleMenuClose = this.handleMenuClose.bind(this)
	}

	hasOneOf(value, ...args) {
		const state = args[args.length - 2]
		const props = args[args.length - 1]

		const values = args.map(item => {
			if (typeof item === 'string') {
				const parts = item.split('.')

				if (parts.length > 1) {
					if (parts[0] === 'props') {
						return parts.length === 2 ? props[parts[1]] : props[parts[1]][parts[2]]
					} else {
						return parts.length === 2 ? state[parts[1]] : state[parts[1]][parts[2]]
					}
				}
			}

			return ''
		}).filter(item => item !== undefined && item !== '')

		return values.length > 0
	}

	handleMenuOpen(event) {
		this.setState({ anchorEl: event.currentTarget })
	}

	handleMenuClose() {
		this.setState({ anchorEl: null })
	}

	handleKeyPress(event) {
		if (event.key === 'Enter') {
			this.props.search(this.props.searchObject)
		}
	}

	onClear(event) {
		this.props.cancel()

		this.setState({isClearing: true})
	}

	async onSearchClick(event) {
		const validation = await this.validator.validate(this.state, this.props)
		this.setState({ validation })

		if (validation.isValid) {
			const { search, searchObject } = this.props

			search(searchObject)
		}
	}

	onNewAccount(event) {
		this.setState({ newAccountNumber: '', openNewAccountDialog: true })

		this.newAccountDialog.current.setCountry(this.props.searchObject.country)
	}

	closeNewAccountDialog(event) {
		this.setState({ openNewAccountDialog: false })
	}

	async createNewAccount(accountNumber, accountName, dealerLocationId, address) {
		this.setState({ openNewAccountDialog: false })

		try {
			let addressResponse = await api.service('addresses').create({
				address: address.address,
				address2: address.address2,
				city: address.city,
				state: address.state,
				zip: address.zip,
				countryId: address.countryId ? address.countryId : 'USA'
			})

			let localAccountResponse = await api.service('localAccounts').create({
				accountNumber: accountNumber,
				name: accountName,
				addressId: addressResponse.id,
				dealerLocationId: dealerLocationId
			})

			this.props.loadPref(localAccountResponse.id, true)
		}
		catch (error) {
			toast.error("There was a problem writing to the database", { autoClose: false })
		}
	}

	render() {
		const { classes, roles, logout, searchObject, handleChangeValue, handleChangeValueSelect } = this.props
		const { anchorEl, isClearing, validation } = this.state

		if (isClearing) {
			this.setState({isClearing: false})
			
			return (
				<Grid container justify="center" className={classes.saving} alignItems="center" alignContent="center">
					<CircularProgress size={50} className={classes.button} />
					<Typography variant="display3" >Clearing...</Typography>
				</Grid>
			)
		}

		if (this.props.goBack) {
			return <Redirect to="/home" />
		}

		if (searchObject.accountType === undefined) {
			if (roles.includes('eagle') || roles.includes('eagle_supervisor')) {
				searchObject.accountType = 'LBB'
			} else {
				searchObject.accountType = '-'
			}
		}
		
		return (
			<Page>
				<ToastContainer position="top-center" />

				<AccountSelectionDialog
					roles={this.props.roles}
					open={this.props.subOpen || this.props.subNafaOpen}
					isLbb={this.props.searchObject.accountType === 'LBB'}
					lbbFlag={this.props.lbbFlag}
					onClose={this.props.closeNestedDialog}
					onRowClick={this.props.loadPref}
					onNewAccount={this.onNewAccount}
					moreResults={this.props.moreResults}
					data={this.props.nafaData}
					isEmptySearch={Object.keys(this.props.searchObject).length <= 1} />

				<NewAccountDialog
					ref={this.newAccountDialog}
					countryTypeOptions={this.props.countryTypeOptions}
					open={this.state.openNewAccountDialog}
					onClose={this.closeNewAccountDialog}
					onNew={this.createNewAccount} />

				<AppBar fixed>
					<Avatar src="/bib.jpg" className={classes.logo} />

					<IconButton className={classes.menuButton} aria-haspopup='true' onClick={this.handleMenuOpen}>
						<MenuIcon />
					</IconButton>

					<Menu 
						id="menu-appbar" 
						anchorEl={anchorEl}
						keepMounted
						open={Boolean(anchorEl)} 
						onClose={this.handleMenuClose}
						transformOrigin={{
							vertical: 'top',
							horizontal: 'left',
						}}
					>
						<Link to='/inbound' style={{ textDecoration: 'none', outline: 'none', color: 'inherit' }}>
							<MenuItem>
								<SettingsPhoneIcon className={clsx(classes.leftIcon, classes.iconSmall)} />Inbound Program Maintenance
							</MenuItem>
						</Link>

						<MenuItem onClick={() => logout()}>
							<ExitToAppIcon className={clsx(classes.leftIcon, classes.iconSmall)} />Logout
						</MenuItem>
					</Menu>

					<div style={{ display: 'inline-block', flexGrow: 1 }}>
						<span style={{ fontWeight: 700, marginRight: '.5rem' }}>ERS Profile</span>
						<span> Search</span>
					</div>

					<Typography align="right" color="inherit" className={[classes.flex, classes.userInfo].join(' ')}>
						Welcome, {this.props.username}
					</Typography>
				</AppBar>

				<Container style={{ marginTop: 81 }}>
					<Grid container spacing={1}>
						<Grid item xs={12}>
							<Grid container spacing={1}>
								<Grid item xs={4} md={2} xl={1}>
									<MaterialSelectable
										name="accountType"
										label="Account Type"
										value={this.props.searchObject.accountType}
										onChange={handleChangeValueSelect}
										options={this.props.accountTypeOptions} />
								</Grid>
								<Grid item xs={4} md={3} xl={2}>
									<MaterialSelectable
										name="country"
										label="Country"
										value={this.props.searchObject.country}
										onChange={handleChangeValueSelect}
										options={this.props.countryTypeOptions} />
								</Grid>
							</Grid>
						</Grid>
						<Grid item xs={12}>
							<Grid container spacing={1}>
								<Grid item xs={12} sm={6} md={4} lg={3}>
									<MaterialInputable
										name="accountNumber"
										label={this.props.searchObject.accountType === 'LBB' ? "Fleet Store Account Number" : "Fleet Bill To or Ship To"}
										value={this.props.searchObject.accountNumber}
										error={validation.accountNumber.isInvalid}
										errorMessage={validation.accountNumber.message}
										onChange={handleChangeValue}
										onKeyPress={this.handleKeyPress} />
								</Grid>
							</Grid>
						</Grid>
						<Grid item xs={12}>
							<Grid container spacing={1}>
								<Grid item xs={12} sm={6} md={4} lg={3}>
									<MaterialInputable
										name="customerName"
										label="Customer Name"
										value={this.props.searchObject.customerName}
										error={validation.accountNumber.isInvalid}
										errorMessage={validation.accountNumber.message}
										onChange={handleChangeValue}
										onKeyPress={this.handleKeyPress} />
								</Grid>

								<Grid item xs={12} sm={6} md={8} lg={5}>
									<Address values={this.props.searchObject} names={address} onChange={handleChangeValue} onSelectChange={handleChangeValueSelect} layout='table' />
								</Grid>
							</Grid>
						</Grid>
						{this.props.searchObject.accountType === 'LBB' &&
							<Grid item xs={12}>
							<Grid container spacing={1}>
									<Grid item xs={12} sm={6} md={4} lg={3}>
										<MaterialInputable
											name="dealerName"
											label="Dealer Name"
											value={this.props.searchObject.dealerName}
											error={validation.accountNumber.isInvalid}
											errorMessage={validation.accountNumber.message}
											onChange={handleChangeValue}
											onKeyPress={this.handleKeyPress} />
									</Grid>

									<Grid item xs={12} sm={6} md={8} lg={5}>
										<Address values={this.props.searchObject} names={dealerAddress} onChange={handleChangeValue} onSelectChange={handleChangeValueSelect} layout='table' />
									</Grid>
								</Grid>
							</Grid>
						}
						<Grid item xs={12}>
							<Grid container spacing={1}>
								<Grid item>
									<Button color="primary" onClick={this.onSearchClick}>
										<SearchIcon className={classes.extendedIcon} />
										Search
									</Button>
								</Grid>
								<Grid item>
									<Button color="danger" onClick={this.onClear}>
										<ClearIcon className={classes.extendedIcon} />
										Clear
									</Button>
								</Grid>
							</Grid>
						</Grid>
					</Grid>
				</Container>
			</Page>
		)
	}
}

FormDialog.propTypes = {
	classes: PropTypes.object,
	environment: PropTypes.string,
	username: PropTypes.string,
	roles: PropTypes.array,
	logout: PropTypes.func,
	open: PropTypes.bool,
	subOpen: PropTypes.bool,
	subNafaOpen: PropTypes.bool,
	searchObject: PropTypes.object,
	isLbb: PropTypes.bool,
	lbbFlag: PropTypes.bool,
	lbbId: PropTypes.string,
	nafaId: PropTypes.string,
	search: PropTypes.func,
	goBack: PropTypes.bool,
	cancel: PropTypes.func,
	nafaData: PropTypes.array,
	moreResults: PropTypes.bool,
	loadPref: PropTypes.func,
	handleChangeValue: PropTypes.func,
	handleChangeValueSelect: PropTypes.func,
	closeNestedDialog: PropTypes.func,
	accountTypeOptions: PropTypes.array,
	countryTypeOptions: PropTypes.array,
}

export default withStyles(styles)(FormDialog)


// WEBPACK FOOTER //
// ./src/components/FormDialog.js